import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import styles from "./Mobilecalendar.module.css";
import {
  Add24Regular,
  ArrowLeft24Regular,
  ArrowRepeatAll16Regular,
  ArrowRepeatAll24Regular,
  Building24Regular,
  CalendarAdd24Regular,
  CalendarDay24Regular,
  CalendarLtr24Filled,
  CalendarLtr24Regular,
  CalendarToday24Regular,
  Checkmark24Regular,
  Clock24Regular,
  Delete24Regular,
  Dismiss24Regular,
  Edit24Regular,
  Grid24Regular,
  Location24Regular,
  Mail24Regular,
  Navigation24Regular,
  People24Regular,
  Search24Regular,
  TextBulletListSquare24Regular,
  TextDescription24Regular,
  Video24Regular,
} from "@fluentui/react-icons";
import useAuth from "../../../hooks/useAuth";

/* =====================================================================
   Types (same shapes the desktop calendar uses)
   ===================================================================== */
type Repeat = "none" | "daily" | "weekdays" | "weekly" | "monthly" | "yearly" | "custom";
type View = "agenda" | "day";
type Status = "loading" | "ready" | "error" | "signin";

interface CalDef {
  id: string;
  name: string;
  color: string;
  on: boolean;
  canEdit: boolean;
}
interface Guest {
  name: string;
  email: string;
  userId: string | null;
}
interface Ev {
  id: string;
  calId: string;
  title: string;
  date: string;
  seriesDate: string;
  startAt: string;
  start: number;
  end: number;
  repeat: Repeat;
  recurring: boolean;
  location: string;
  online: boolean;
  onlineUrl: string;
  organizer: string;
  attendees: string[];
  guests: Guest[];
  notes: string;
  canEdit: boolean;
}
interface Occ {
  ev: Ev;
  key: string;
  day: Date;
  endAt: Date;
  col: number;
  cols: number;
}
interface Draft {
  id: string | null;
  title: string;
  calId: string;
  date: string;
  start: string;
  end: string;
  repeat: Repeat;
  was: Repeat;
  location: string;
  online: boolean;
  attendees: string;
  notes: string;
}
type Toast = { id: number; msg: string; undo: (() => void) | null } | null;

/* =====================================================================
   Dates
   ===================================================================== */
const HOUR_H = 64;
const TEAMS = "Microsoft Teams Meeting";
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DOW_LONG = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const DOW_INITIAL = ["S", "M", "T", "W", "T", "F", "S"];
const SWATCHES = ["#0f6cbd", "#00a858", "#f7630c", "#8764b8", "#c4314b", "#038387"];

const pad = (n: number) => String(n).padStart(2, "0");
const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseYmd = (s: string): Date => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
};
const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const startOfWeek = (d: Date) => addDays(d, -d.getDay());
const sameDay = (a: Date, b: Date) => ymd(a) === ymd(b);
const hhmm = (m: number) => `${pad(Math.floor(m / 60))}:${pad(m % 60)}`;
const toMin = (s: string) => {
  const [h, m] = s.split(":").map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
};
const longDate = (d: Date) => `${DOW_LONG[d.getDay()] ?? ""}, ${d.getDate()} ${MONTHS[d.getMonth()] ?? ""} ${d.getFullYear()}`;
const isAllDay = (e: Ev) => e.start === 0 && e.end === 24 * 60;
const durText = (e: Ev) => {
  if (isAllDay(e)) return "All day";
  const m = e.end - e.start;
  if (m < 60) return `${m}m`;
  return m % 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m / 60}h`;
};
const repeatText = (e: Ev): string => {
  if (e.repeat === "daily") return "Repeats every day";
  if (e.repeat === "weekdays") return "Repeats every weekday";
  if (e.repeat === "weekly") return `Repeats every ${DOW_LONG[parseYmd(e.seriesDate).getDay()] ?? "week"}`;
  if (e.repeat === "monthly") return "Repeats every month";
  if (e.repeat === "yearly") return "Repeats every year";
  return "Repeats on a custom schedule";
};
const initialsOf = (name: string) => {
  const parts = name.replace(/[^\p{L}\p{N}\s]/gu, " ").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};
const AVATAR_COLORS = ["#0f6cbd", "#7a3fb5", "#1e7a3c", "#c2570c", "#0b6a6a", "#b8791a", "#c4314b", "#2b5fd9"];
const colorOf = (s: string) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
};

/** Packs a day's events into side-by-side columns when they overlap. */
const layoutDay = (items: Occ[]): Occ[] => {
  const sorted = [...items].sort((a, b) => a.ev.start - b.ev.start || b.ev.end - a.ev.end);
  const out: Occ[] = [];
  let cluster: Occ[] = [];
  let colEnds: number[] = [];
  let clusterEnd = -1;
  const flush = () => {
    const n = Math.max(colEnds.length, 1);
    cluster.forEach((o) => {
      o.cols = n;
    });
    out.push(...cluster);
    cluster = [];
    colEnds = [];
    clusterEnd = -1;
  };
  for (const it of sorted) {
    if (cluster.length > 0 && it.ev.start >= clusterEnd) flush();
    let c = colEnds.findIndex((e) => e <= it.ev.start);
    if (c < 0) {
      c = colEnds.length;
      colEnds.push(it.ev.end);
    } else {
      colEnds[c] = it.ev.end;
    }
    cluster.push({ ...it, col: c, cols: 1 });
    clusterEnd = Math.max(clusterEnd, it.ev.end);
  }
  flush();
  return out;
};
const makeOcc = (ev: Ev, day: Date): Occ => ({ ev, key: ymd(day), day, endAt: new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, ev.end), col: 0, cols: 1 });

/* =====================================================================
   Calendar service (calender.cilpron.com) - the same calls the desktop calendar makes
   ===================================================================== */
const CAL_BASE_URL = "https://calender.cilpron.com";
const TZ = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "Africa/Lagos";
  } catch {
    return "Africa/Lagos";
  }
})();

let currentToken: string | null = null;
const readStoredToken = (): string | null => {
  try {
    return localStorage.getItem("authToken");
  } catch {
    return null;
  }
};

class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}
const enc = encodeURIComponent;

async function call<T>(method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE", path: string, body?: unknown): Promise<T> {
  const token = currentToken;
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  if (body !== undefined) headers["Content-Type"] = "application/json";
  let res: Response;
  try {
    res = await fetch(`${CAL_BASE_URL}/api/calendar${path}`, { method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
  } catch {
    throw new ApiError(0, "Couldn’t reach the calendar. Check your connection.");
  }
  if (res.status === 401) throw new ApiError(401, "Your session ended. Sign in again.");
  if (res.status === 403) throw new ApiError(403, "You don’t have permission to do that.");
  if (!res.ok) {
    let msg = "";
    try {
      const j = (await res.json()) as Record<string, unknown>;
      const pick = j.detail ?? j.message ?? j.title ?? j.error;
      if (typeof pick === "string") msg = pick;
    } catch {
      /* no readable body */
    }
    throw new ApiError(res.status, res.status === 400 ? msg || "That could not be saved. Check the entries and try again." : msg || `The calendar answered with an error (${res.status}).`);
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}
const msgOf = (e: unknown): string => (e instanceof ApiError ? e.message : "Something went wrong. Try again.");

interface ApiGuest {
  name?: string | null;
  email?: string | null;
  userId?: string | null;
}
interface ApiEvent {
  id: string;
  calendarId: string;
  title: string;
  description?: string | null;
  location?: string | null;
  startAt: string;
  endAt: string;
  seriesStartAt?: string | null;
  isAllDay: boolean;
  repeat?: string;
  isRecurring: boolean;
  organizerName?: string | null;
  createdBy?: string;
  onlineProvider?: string | null;
  onlineUrl?: string | null;
  canEdit: boolean;
  attendees?: ApiGuest[];
}
interface ApiCalendar {
  id: string;
  name: string;
  color?: string | null;
  role?: string;
}

const REPEATS: Repeat[] = ["none", "daily", "weekdays", "weekly", "monthly", "yearly", "custom"];
const minutesOf = (d: Date) => d.getHours() * 60 + d.getMinutes();

const tokenSub = (token: string | null): string | undefined => {
  try {
    if (!token) return undefined;
    const p = token.split(".")[1] ?? "";
    const json = decodeURIComponent(
      atob(p.replace(/-/g, "+").replace(/_/g, "/"))
        .split("")
        .map((c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0"))
        .join("")
    );
    const o = JSON.parse(json) as Record<string, string>;
    return (o.sub ?? o.nameid ?? o["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"])?.toLowerCase();
  } catch {
    return undefined;
  }
};

const toEv = (a: ApiEvent, me: string | undefined): Ev => {
  const s = new Date(a.startAt);
  const e = new Date(a.endAt);
  const date = a.isAllDay ? a.startAt.slice(0, 10) : ymd(s);
  const start = a.isAllDay ? 0 : minutesOf(s);
  let end = a.isAllDay ? 24 * 60 : ymd(e) === date ? minutesOf(e) : 24 * 60;
  if (end <= start) end = Math.min(start + 15, 24 * 60);
  const series = a.seriesStartAt ? new Date(a.seriesStartAt) : s;
  const provider = (a.onlineProvider ?? "none").toLowerCase();
  const online = provider !== "none" && provider !== "";
  const guests: Guest[] = (a.attendees ?? [])
    .map((g) => ({ name: (g.name ?? "").trim(), email: (g.email ?? "").trim(), userId: g.userId ?? null }))
    .filter((g) => g.name || g.email);
  const repeat = (REPEATS.includes(a.repeat as Repeat) ? a.repeat : a.isRecurring ? "custom" : "none") as Repeat;
  return {
    id: a.id,
    calId: a.calendarId,
    title: a.title,
    date,
    seriesDate: a.isAllDay ? (a.seriesStartAt ?? a.startAt).slice(0, 10) : ymd(series),
    startAt: a.startAt,
    start,
    end,
    repeat,
    recurring: a.isRecurring,
    location: (a.location ?? "") || (online ? (provider === "teams" ? TEAMS : "Online meeting") : ""),
    online,
    onlineUrl: a.onlineUrl ?? "",
    organizer: me && (a.createdBy ?? "").toLowerCase() === me ? "You" : a.organizerName || "Organizer",
    attendees: guests.map((g) => g.name || g.email),
    guests,
    notes: a.description ?? "",
    canEdit: a.canEdit,
  };
};
const toCal = (c: ApiCalendar, i: number, was: CalDef | undefined): CalDef => ({
  id: c.id,
  name: c.name,
  color: c.color || SWATCHES[i % SWATCHES.length] || "#0f6cbd",
  on: was ? was.on : true,
  canEdit: ["owner", "manager", "editor"].includes((c.role ?? "").toLowerCase()),
});

/* =====================================================================
   The app
   ===================================================================== */
const Avatar: React.FC<{ name: string; size?: number }> = ({ name, size = 36 }) => (
  <span className={styles.avatar} style={{ width: size, height: size, background: `${colorOf(name)}22`, color: colorOf(name), fontSize: size * 0.38 }}>
    {initialsOf(name)}
  </span>
);

export interface MobileCalendarProps {
  token?: string | null;
  displayName?: string;
  email?: string;
  onExit?: () => void;
}

export const MobileCalendarClient: React.FC<MobileCalendarProps> = ({ token = null, displayName = "", email = "", onExit }) => {
  currentToken = token;

  const [now, setNow] = useState(() => new Date());
  const today = useMemo(() => startOfDay(now), [now]);
  const [anchor, setAnchor] = useState<Date>(() => startOfDay(new Date()));
  const [view, setView] = useState<View>("agenda");
  const [monthOpen, setMonthOpen] = useState(false);
  const [cals, setCals] = useState<CalDef[]>([]);
  const [events, setEvents] = useState<Ev[]>([]);
  const [status, setStatus] = useState<Status>("loading");
  const [note, setNote] = useState("");
  const [drawer, setDrawer] = useState(false);
  const [sheet, setSheet] = useState<null | "account" | "addCal" | "delete">(null);
  const [toast, setToast] = useState<Toast>(null);
  const toastId = useRef(0);
  const toastTimer = useRef<number | undefined>(undefined);
  const [newCal, setNewCal] = useState({ name: "", color: SWATCHES[0] as string });

  const [paneEv, setPaneEv] = useState<{ id: string; date: string } | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [draftErr, setDraftErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [delEv, setDelEv] = useState<Ev | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const [stack, setStack] = useState<string[]>([]);
  const stackRef = useRef<string[]>([]);
  stackRef.current = stack;

  const listRef = useRef<HTMLDivElement>(null);
  const dayScrollRef = useRef<HTMLDivElement>(null);
  const seq = useRef(0);
  const loadRef = useRef<(quiet?: boolean) => Promise<void>>(async () => undefined);
  const rangeRef = useRef({ from: "", to: "" });
  const swipe = useRef<{ x: number; y: number } | null>(null);
  const swiped = useRef(false);
  const dragY = useRef<number | null>(null);

  /* ---------- history: Android back / swipe-back closes pages ---------- */
  useEffect(() => {
    const onPop = () => {
      const top = stackRef.current[stackRef.current.length - 1];
      setStack((s) => s.slice(0, -1));
      if (top === "event") setPaneEv(null);
      if (top === "edit") setDraft(null);
      if (top === "search") setSearchOpen(false);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const pushPage = (name: "event" | "edit" | "search") => {
    window.history.pushState({ mc: name }, "");
    setStack((s) => [...s, name]);
  };
  const popPage = () => {
    if (stackRef.current.length) window.history.back();
  };
  const popAll = () => {
    const n = stackRef.current.length;
    if (n) window.history.go(-n);
  };

  const say = useCallback((msg: string, undo?: () => void) => {
    const id = ++toastId.current;
    setToast({ id, msg, undo: undo ?? null });
    window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast((t) => (t && t.id === id ? null : t)), undo ? 6500 : 3200);
  }, []);
  useEffect(() => () => window.clearTimeout(toastTimer.current), []);

  useEffect(() => {
    const t = window.setInterval(() => setNow(new Date()), 60000);
    return () => window.clearInterval(t);
  }, []);

  /* a phone shows the page zoomed out when it has no viewport tag, so make sure one is set */
  useEffect(() => {
    let m = document.querySelector('meta[name="viewport"]') as HTMLMetaElement | null;
    const created = !m;
    const prev = m ? m.getAttribute("content") : null;
    if (!m) {
      m = document.createElement("meta");
      m.name = "viewport";
      document.head.appendChild(m);
    }
    m.setAttribute("content", "width=device-width, initial-scale=1, viewport-fit=cover");
    return () => {
      if (!m) return;
      if (created) m.remove();
      else if (prev !== null) m.setAttribute("content", prev);
    };
  }, []);

  /* ---------- what to load: the anchor's month grid plus three more weeks ---------- */
  const monthFirst = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const gridStart = startOfWeek(monthFirst);
  const rangeEnd = addDays(gridStart, 63);
  rangeRef.current = { from: gridStart.toISOString(), to: rangeEnd.toISOString() };
  const rangeKey = ymd(gridStart);

  const load = useCallback(async (quiet = false) => {
    if (!currentToken) {
      setNote("");
      setStatus("signin");
      return;
    }
    const mine = ++seq.current;
    if (!quiet) setStatus((s) => (s === "ready" ? s : "loading"));
    try {
      const r = rangeRef.current;
      const feed = await call<ApiEvent[]>("GET", `/feed?from=${enc(r.from)}&to=${enc(r.to)}`); // first: a new person gets a default calendar here
      const list = await call<ApiCalendar[]>("GET", "/calendars");
      if (mine !== seq.current) return;
      const who = tokenSub(currentToken);
      setCals((prev) => list.map((c, i) => toCal(c, i, prev.find((p) => p.id === c.id))));
      setEvents(feed.map((a) => toEv(a, who)));
      setNote("");
      setStatus("ready");
    } catch (e) {
      if (mine !== seq.current) return;
      setNote(msgOf(e));
      setStatus(e instanceof ApiError && e.status === 401 ? "signin" : "error");
    }
  }, []);
  loadRef.current = load;

  useEffect(() => {
    void load();
  }, [load, rangeKey, token]);

  useEffect(() => {
    if (status !== "error") return;
    const t = window.setTimeout(() => void loadRef.current(true), 15000);
    return () => window.clearTimeout(t);
  }, [status]);

  useEffect(() => {
    const back = () => {
      if (!document.hidden) void loadRef.current(true);
    };
    window.addEventListener("focus", back);
    document.addEventListener("visibilitychange", back);
    return () => {
      window.removeEventListener("focus", back);
      document.removeEventListener("visibilitychange", back);
    };
  }, []);

  /* live updates: the service says "changed" and we ask again */
  useEffect(() => {
    if (!token) return;
    let dead = false;
    let again = 0;
    let soon = 0;
    const conn = new HubConnectionBuilder()
      .withUrl(`${CAL_BASE_URL}/hubs/calendar`, { accessTokenFactory: () => currentToken ?? "" })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(LogLevel.None)
      .build();
    const refresh = () => {
      window.clearTimeout(soon);
      soon = window.setTimeout(() => void loadRef.current(true), 400);
    };
    const start = () => {
      if (dead) return;
      conn.start().catch(() => {
        if (!dead) again = window.setTimeout(start, 10000);
      });
    };
    conn.on("changed", refresh);
    conn.onreconnected(refresh);
    conn.onclose(() => {
      if (!dead) again = window.setTimeout(start, 10000);
    });
    start();
    return () => {
      dead = true;
      window.clearTimeout(again);
      window.clearTimeout(soon);
      void conn.stop();
    };
  }, [token]);

  /* ---------- derived ---------- */
  const hidden = useMemo(() => new Set(cals.filter((c) => !c.on).map((c) => c.id)), [cals]);
  const byDate = useMemo(() => {
    const m = new Map<string, Ev[]>();
    for (const e of events) {
      if (hidden.has(e.calId)) continue;
      const l = m.get(e.date);
      if (l) l.push(e);
      else m.set(e.date, [e]);
    }
    m.forEach((l) => l.sort((a, b) => a.start - b.start || a.end - b.end));
    return m;
  }, [events, hidden]);
  const calById = (id: string) => cals.find((c) => c.id === id);
  const writable = cals.filter((c) => c.canEdit);
  const evsOn = (d: Date) => byDate.get(ymd(d)) ?? [];

  const agendaDays = useMemo(() => {
    const startKey = ymd(startOfWeek(anchor));
    const keys = new Set<string>([ymd(anchor)]);
    byDate.forEach((_, k) => {
      if (k >= startKey) keys.add(k);
    });
    return Array.from(keys).sort().map(parseYmd);
  }, [byDate, anchor]);

  const stripDays = Array.from({ length: 7 }, (_, i) => addDays(startOfWeek(anchor), i));
  const gridDays = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));

  const title = `${MONTHS[anchor.getMonth()] ?? ""}${anchor.getFullYear() !== today.getFullYear() ? ` ${anchor.getFullYear()}` : ""}`;

  const goTo = (d: Date) => setAnchor(startOfDay(d));

  /* bring the chosen day to the top of the agenda */
  const anchorKey = ymd(anchor);
  useEffect(() => {
    if (view !== "agenda") return;
    const box = listRef.current;
    const el = box?.querySelector<HTMLElement>(`[data-day="${anchorKey}"]`);
    if (box && el) box.scrollTo({ top: el.offsetTop - box.offsetTop - 4, behavior: "smooth" });
  }, [anchorKey, view, status]);

  /* day view starts at the first event, the current time, or 08:00 */
  useEffect(() => {
    if (view !== "day" || !dayScrollRef.current) return;
    const list = evsOn(anchor).filter((e) => !isAllDay(e));
    const m = list.length ? (list[0] as Ev).start : sameDay(anchor, today) ? minutesOf(now) - 60 : 8 * 60;
    dayScrollRef.current.scrollTop = Math.max(0, (m / 60) * HOUR_H - 24);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, anchorKey]);

  /* ---------- gestures ---------- */
  const swipeProps = (onLeft: () => void, onRight: () => void) => ({
    onPointerDown: (e: React.PointerEvent) => {
      swipe.current = { x: e.clientX, y: e.clientY };
      swiped.current = false;
    },
    onPointerUp: (e: React.PointerEvent) => {
      const s = swipe.current;
      swipe.current = null;
      if (!s) return;
      const dx = e.clientX - s.x;
      const dy = e.clientY - s.y;
      if (Math.abs(dx) > 60 && Math.abs(dy) < 40) {
        swiped.current = true;
        window.setTimeout(() => (swiped.current = false), 250);
        if (dx < 0) onLeft();
        else onRight();
      }
    },
    onPointerCancel: () => {
      swipe.current = null;
    },
  });
  const pickDay = (d: Date) => {
    if (swiped.current) return;
    goTo(d);
    setMonthOpen(false);
  };

  /* ---------- events: open, create, edit, delete ---------- */
  const paneEvent = paneEv ? events.find((e) => e.id === paneEv.id && e.date === paneEv.date) : undefined;
  const openEvent = (e: Ev) => {
    setPaneEv({ id: e.id, date: e.date });
    pushPage("event");
  };
  const blankDraft = (day: Date, startMin: number, online = false): Draft => ({
    id: null,
    title: "",
    calId: (writable.find((c) => c.on) ?? writable[0])?.id ?? "",
    date: ymd(day),
    start: hhmm(startMin),
    end: hhmm(Math.min(startMin + 30, 24 * 60 - 1)),
    repeat: "none",
    was: "none",
    location: online ? TEAMS : "",
    online,
    attendees: "",
    notes: "",
  });
  const openNew = () => {
    if (writable.length === 0) {
      say(status === "ready" ? "You have no calendar you can add events to." : "Your calendars are still loading.");
      return;
    }
    let s = 9 * 60;
    if (sameDay(anchor, today)) s = Math.min(Math.ceil((now.getHours() * 60 + now.getMinutes() + 1) / 30) * 30, 23 * 60);
    setDraftErr("");
    setDraft(blankDraft(anchor, s));
    pushPage("edit");
  };
  const openEdit = (ev: Ev) => {
    if (!ev.canEdit) {
      say("You can only view this event.");
      return;
    }
    setDraftErr("");
    setDraft({
      id: ev.id,
      title: ev.title,
      calId: ev.calId,
      date: ev.recurring ? ev.seriesDate : ev.date,
      start: hhmm(ev.start),
      end: hhmm(ev.end),
      repeat: ev.repeat,
      was: ev.repeat,
      location: ev.location,
      online: ev.online,
      attendees: ev.attendees.join(", "),
      notes: ev.notes,
    });
    pushPage("edit");
  };
  const patchDraft = (p: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...p } : d));

  const saveDraft = async () => {
    if (!draft || saving) return;
    const t = draft.title.trim();
    if (!t) return setDraftErr("Add a title for the event.");
    if (!draft.date) return setDraftErr("Pick a date.");
    if (!draft.calId) return setDraftErr("Pick a calendar.");
    const s = toMin(draft.start);
    const e = toMin(draft.end);
    if (e <= s) return setDraftErr("The end time must be after the start time.");
    const day = parseYmd(draft.date);
    const at = (m: number) => new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, m).toISOString();
    const known = draft.id === null ? [] : events.find((x) => x.id === draft.id)?.guests ?? [];
    const attendees = draft.attendees
      .split(",")
      .map((a) => a.trim())
      .filter(Boolean)
      .map((n) => {
        const g = known.find((k) => (k.name || k.email).toLowerCase() === n.toLowerCase());
        return g ? { name: g.name, email: g.email, userId: g.userId } : { name: n, email: "" }; // a name with no e-mail is listed but never e-mailed
      });
    const body: Record<string, unknown> = {
      calendarId: draft.calId,
      title: t,
      description: draft.notes.trim(),
      location: draft.online && !draft.location.trim() ? TEAMS : draft.location.trim(),
      startAt: at(s),
      endAt: at(e),
      isAllDay: false,
      timeZone: TZ,
      onlineProvider: draft.online ? "Teams" : "None",
      attendees,
    };
    if (draft.repeat !== "custom" && (draft.id === null ? draft.repeat !== "none" : draft.repeat !== draft.was)) body.recurrence = draft.repeat;
    setSaving(true);
    try {
      if (draft.id === null) await call("POST", "/events", body);
      else await call("PATCH", `/events/${draft.id}?scope=all`, body);
      const created = draft.id === null;
      const when = draft.date;
      say(created ? "Event created" : "Event updated");
      popAll();
      goTo(parseYmd(when));
      void load(true);
    } catch (err) {
      setDraftErr(msgOf(err));
    } finally {
      setSaving(false);
    }
  };

  const undoDelete = async (id: string) => {
    try {
      await call("POST", `/events/${id}/restore`);
      say("Event restored");
    } catch (err) {
      say(msgOf(err));
    }
    void load(true);
  };
  const removeEvent = async (ev: Ev, scope: "this" | "all") => {
    setSheet(null);
    setDelEv(null);
    popAll();
    const prev = events;
    try {
      if (scope === "this") {
        setEvents((p) => p.filter((x) => !(x.id === ev.id && x.date === ev.date)));
        await call("DELETE", `/events/${ev.id}?scope=this&occurrence=${enc(ev.startAt)}`);
        say("Event deleted");
      } else {
        setEvents((p) => p.filter((x) => x.id !== ev.id));
        await call("DELETE", `/events/${ev.id}?undo=true`); // hidden for a short while; nobody is told until the Undo time has passed
        say("Event deleted", () => void undoDelete(ev.id));
      }
    } catch (err) {
      setEvents(prev);
      say(msgOf(err));
    }
  };
  const askDelete = (ev: Ev) => {
    if (ev.recurring) {
      setDelEv(ev);
      setSheet("delete");
    } else void removeEvent(ev, "all");
  };
  const saveCal = async () => {
    const name = newCal.name.trim();
    if (!name) return;
    try {
      await call("POST", "/calendars", { name, color: newCal.color, kind: "personal" });
      setSheet(null);
      setNewCal({ name: "", color: SWATCHES[0] as string });
      say(`“${name}” calendar added`);
      void load(true);
    } catch (err) {
      say(msgOf(err));
    }
  };
  const toggleCal = (id: string) => setCals((p) => p.map((c) => (c.id === id ? { ...c, on: !c.on } : c)));
  const openMail = () => window.dispatchEvent(new CustomEvent("cilpron:open-mobile-app", { detail: "email" }));

  /* ---------- search over what is loaded ---------- */
  const results = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return [];
    return events
      .filter((e) => !hidden.has(e.calId) && [e.title, e.location, e.organizer, e.notes, ...e.attendees].some((x) => x.toLowerCase().includes(t)))
      .sort((a, b) => (a.date === b.date ? a.start - b.start : a.date < b.date ? -1 : 1))
      .slice(0, 80);
  }, [q, events, hidden]);

  /* ---------- pieces ---------- */
  const eventCard = (e: Ev, past: boolean) => {
    const c = calById(e.calId)?.color ?? "#0f6cbd";
    return (
      <button key={`${e.id}|${e.date}`} type="button" className={`${styles.evRow} ${past ? styles.past : ""}`} onClick={() => openEvent(e)}>
        <span className={styles.evTime}>
          <b>{isAllDay(e) ? "All day" : hhmm(e.start)}</b>
          {!isAllDay(e) && <small>{durText(e)}</small>}
        </span>
        <span className={`${styles.evCard} ${e.recurring ? styles.evHatch : ""}`} style={{ "--cc": c } as React.CSSProperties}>
          <span className={styles.evTitle}>{e.title || "(No title)"}</span>
          {(e.location || e.online) && <span className={styles.evSub}>{e.location}</span>}
          {e.recurring && <ArrowRepeatAll16Regular className={styles.evRepeat} />}
        </span>
      </button>
    );
  };

  const dayHead = (d: Date) => (
    <div className={`${styles.dayHead} ${sameDay(d, today) ? styles.dayToday : ""}`}>
      <span>
        {d.getDate()} {DOW_LONG[d.getDay()]}
      </span>
      {sameDay(d, today) && <em>Today</em>}
    </div>
  );

  const agenda = (
    <div className={styles.agenda} ref={listRef}>
      {status === "loading" && events.length === 0 && (
        <div aria-busy="true">
          {[0, 1, 2].map((i) => (
            <div key={i} className={styles.skel}>
              <span />
              <span />
            </div>
          ))}
        </div>
      )}
      {(status === "error" || status === "signin") && (
        <div className={styles.notice}>
          <b>{status === "signin" ? "Sign in to see your calendar" : "Couldn’t load your calendar"}</b>
          <span>{note || (status === "signin" ? "Your session isn’t available right now." : "")}</span>
          {status === "error" && (
            <button type="button" className={styles.linkBtn} onClick={() => void load()}>
              Try again
            </button>
          )}
        </div>
      )}
      {status !== "signin" &&
        agendaDays.map((d) => {
          const list = evsOn(d);
          const key = ymd(d);
          return (
            <section key={key} data-day={key} className={styles.daySec}>
              {dayHead(d)}
              {list.length === 0 ? <div className={styles.noEv}>No events</div> : list.map((e) => eventCard(e, ymd(d) < ymd(today) || (sameDay(d, today) && e.end <= minutesOf(now)) ))}
            </section>
          );
        })}
      <div className={styles.endPad} />
    </div>
  );

  const dayOccs = layoutDay(evsOn(anchor).filter((e) => !isAllDay(e)).map((e) => makeOcc(e, anchor)));
  const allDayList = evsOn(anchor).filter(isAllDay);
  const dayView = (
    <div className={styles.dayWrap} {...swipeProps(() => goTo(addDays(anchor, 1)), () => goTo(addDays(anchor, -1)))}>
      {dayHead(anchor)}
      {allDayList.length > 0 && <div className={styles.allDay}>{allDayList.map((e) => eventCard(e, false))}</div>}
      <div className={styles.dayScroll} ref={dayScrollRef}>
        <div className={styles.grid} style={{ height: 24 * HOUR_H }}>
          {Array.from({ length: 24 }, (_, h) => (
            <div key={h} className={styles.hour} style={{ top: h * HOUR_H }}>
              <span>{h === 0 ? "" : `${pad(h)}:00`}</span>
            </div>
          ))}
          {sameDay(anchor, today) && (
            <div className={styles.nowLine} style={{ top: (minutesOf(now) / 60) * HOUR_H }}>
              <i />
            </div>
          )}
          <div className={styles.lane}>
            {dayOccs.map((o) => {
              const e = o.ev;
              const c = calById(e.calId)?.color ?? "#0f6cbd";
              return (
                <button
                  key={`${e.id}|${o.key}`}
                  type="button"
                  className={`${styles.block} ${e.recurring ? styles.evHatch : ""} ${o.endAt.getTime() < now.getTime() ? styles.past : ""}`}
                  style={{ top: (e.start / 60) * HOUR_H + 1, height: Math.max(((e.end - e.start) / 60) * HOUR_H - 2, 24), left: `calc(${(o.col / o.cols) * 100}% + 2px)`, width: `calc(${100 / o.cols}% - 6px)`, "--cc": c } as React.CSSProperties}
                  onClick={() => openEvent(e)}
                >
                  <span className={styles.evTitle}>{e.title || "(No title)"}</span>
                  {e.end - e.start > 30 && <span className={styles.evSub}>{hhmm(e.start)} – {hhmm(e.end)}</span>}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );

  const dot = (d: Date) => byDate.has(ymd(d));
  const header = (
    <header className={`${styles.top} ${monthOpen ? styles.topOpen : ""}`}>
      <div className={styles.bar}>
        <button type="button" className={styles.iconBtn} aria-label="Calendars" onClick={() => setDrawer(true)}>
          <Navigation24Regular />
        </button>
        <button type="button" className={styles.monthBtn} onClick={() => setMonthOpen((v) => !v)} aria-expanded={monthOpen}>
          {title}
        </button>
        {!sameDay(anchor, today) && (
          <button type="button" className={styles.iconBtn} aria-label="Go to today" onClick={() => goTo(today)}>
            <CalendarToday24Regular />
          </button>
        )}
        <button type="button" className={styles.iconBtn} aria-label={view === "agenda" ? "Day view" : "Agenda view"} onClick={() => setView((v) => (v === "agenda" ? "day" : "agenda"))}>
          {view === "agenda" ? <CalendarDay24Regular /> : <TextBulletListSquare24Regular />}
        </button>
        <button
          type="button"
          className={styles.iconBtn}
          aria-label="Search"
          onClick={() => {
            setQ("");
            setSearchOpen(true);
            pushPage("search");
          }}
        >
          <Search24Regular />
        </button>
        <button type="button" className={styles.avatarBtn} aria-label="Account" onClick={() => setSheet("account")}>
          <span className={styles.meAvatar}>{initialsOf(displayName || email)}</span>
        </button>
      </div>

      <div className={styles.dow} aria-hidden="true">
        {DOW_INITIAL.map((c, i) => (
          <span key={i}>{c}</span>
        ))}
      </div>

      {!monthOpen ? (
        <div className={styles.week} {...swipeProps(() => goTo(addDays(anchor, 7)), () => goTo(addDays(anchor, -7)))}>
          {stripDays.map((d) => (
            <button key={ymd(d)} type="button" className={`${styles.dayBtn} ${sameDay(d, anchor) ? styles.daySel : ""} ${sameDay(d, today) ? styles.dayNow : ""}`} aria-label={longDate(d)} aria-current={sameDay(d, today) ? "date" : undefined} onClick={() => pickDay(d)}>
              <span>{d.getDate()}</span>
              <i className={dot(d) ? styles.dotOn : ""} />
            </button>
          ))}
        </div>
      ) : (
        <div className={styles.month} {...swipeProps(() => goTo(new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1)), () => goTo(new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1)))}>
          {gridDays.map((d) => (
            <button
              key={ymd(d)}
              type="button"
              className={`${styles.dayBtn} ${sameDay(d, anchor) ? styles.daySel : ""} ${sameDay(d, today) ? styles.dayNow : ""} ${d.getMonth() !== anchor.getMonth() ? styles.dayOut : ""}`}
              aria-label={longDate(d)}
              onClick={() => pickDay(d)}
            >
              <span>{d.getDate()}</span>
              <i className={dot(d) ? styles.dotOn : ""} />
            </button>
          ))}
        </div>
      )}

      <button
        type="button"
        className={styles.handle}
        aria-label={monthOpen ? "Show one week" : "Show the whole month"}
        onClick={() => setMonthOpen((v) => !v)}
        onPointerDown={(e) => {
          dragY.current = e.clientY;
        }}
        onPointerUp={(e) => {
          const y0 = dragY.current;
          dragY.current = null;
          if (y0 === null) return;
          const dy = e.clientY - y0;
          if (dy > 24) setMonthOpen(true);
          else if (dy < -24) setMonthOpen(false);
        }}
      >
        <span />
      </button>
    </header>
  );

  /* ---------- event page ---------- */
  const ev = paneEvent;
  const evCal = ev ? calById(ev.calId) : undefined;
  const eventPage = (
    <section className={`${styles.page} ${paneEv && stack.includes("event") ? styles.pageOn : ""}`} aria-hidden={!paneEv}>
      <header className={styles.pageBar}>
        <button type="button" className={styles.iconBtn} aria-label="Back" onClick={popPage}>
          <ArrowLeft24Regular />
        </button>
        <span className={styles.grow} />
        {ev && ev.canEdit && (
          <>
            <button type="button" className={styles.iconBtn} aria-label="Edit" onClick={() => openEdit(ev)}>
              <Edit24Regular />
            </button>
            <button type="button" className={styles.iconBtn} aria-label="Delete" onClick={() => askDelete(ev)}>
              <Delete24Regular />
            </button>
          </>
        )}
      </header>
      {ev && (
        <div className={styles.pageScroll}>
          <h2 className={styles.pageTitle} style={{ borderLeftColor: evCal?.color ?? "#0f6cbd" }}>
            {ev.title || "(No title)"}
          </h2>
          <div className={styles.info}>
            <Clock24Regular />
            <div>
              <div>{longDate(parseYmd(ev.date))}</div>
              <small>{isAllDay(ev) ? "All day" : `${hhmm(ev.start)} – ${hhmm(ev.end)} · ${durText(ev)}`}</small>
            </div>
          </div>
          {ev.recurring && (
            <div className={styles.info}>
              <ArrowRepeatAll24Regular />
              <div>{repeatText(ev)}</div>
            </div>
          )}
          {(ev.location || ev.online) && (
            <div className={styles.info}>
              {ev.online ? <Video24Regular /> : <Location24Regular />}
              <div className={styles.grow}>
                <div>{ev.location}</div>
                {ev.online && (
                  <button type="button" className={styles.join} onClick={() => (ev.onlineUrl ? window.open(ev.onlineUrl, "_blank", "noopener") : say("This event has no meeting link yet."))}>
                    Join meeting
                  </button>
                )}
              </div>
            </div>
          )}
          <div className={styles.info}>
            <People24Regular />
            <div className={styles.grow}>
              <div className={styles.person}>
                <Avatar name={ev.organizer} size={32} />
                <span>{ev.organizer}</span>
                <em>Organizer</em>
              </div>
              {ev.attendees.map((a) => (
                <div key={a} className={styles.person}>
                  <Avatar name={a} size={32} />
                  <span>{a}</span>
                </div>
              ))}
            </div>
          </div>
          <div className={styles.info}>
            <CalendarLtr24Regular />
            <div className={styles.calName}>
              <i style={{ background: evCal?.color ?? "#0f6cbd" }} />
              {evCal?.name ?? "Calendar"}
            </div>
          </div>
          {ev.notes && (
            <div className={styles.info}>
              <TextDescription24Regular />
              <div className={styles.notes}>{ev.notes}</div>
            </div>
          )}
        </div>
      )}
    </section>
  );

  /* ---------- new / edit page ---------- */
  const editPage = (
    <section className={`${styles.page} ${styles.pageTop} ${draft && stack.includes("edit") ? styles.pageOn : ""}`} aria-hidden={!draft}>
      <header className={styles.pageBar}>
        <button type="button" className={styles.iconBtn} aria-label="Close" onClick={popPage}>
          <Dismiss24Regular />
        </button>
        <h2 className={styles.barH}>{draft?.id === null ? "New event" : "Edit event"}</h2>
        <button type="button" className={styles.saveBtn} disabled={saving} onClick={() => void saveDraft()}>
          Save
        </button>
      </header>
      {draft && (
        <div className={styles.pageScroll}>
          <input className={styles.titleInput} placeholder="Add a title" value={draft.title} onChange={(e) => patchDraft({ title: e.target.value })} />
          <div className={styles.field}>
            <label>Calendar</label>
            <select value={draft.calId} onChange={(e) => patchDraft({ calId: e.target.value })}>
              {writable.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.field}>
            <label>{draft.repeat !== "none" ? "Series starts" : "Date"}</label>
            <input type="date" value={draft.date} onChange={(e) => patchDraft({ date: e.target.value })} />
          </div>
          <div className={styles.twin}>
            <div className={styles.field}>
              <label>Start</label>
              <input type="time" step={900} value={draft.start} onChange={(e) => patchDraft({ start: e.target.value })} />
            </div>
            <div className={styles.field}>
              <label>End</label>
              <input type="time" step={900} value={draft.end} onChange={(e) => patchDraft({ end: e.target.value })} />
            </div>
          </div>
          <div className={styles.field}>
            <label>Repeat</label>
            <select value={draft.repeat} onChange={(e) => patchDraft({ repeat: e.target.value as Repeat })}>
              <option value="none">Does not repeat</option>
              <option value="daily">Every day</option>
              <option value="weekdays">Every weekday</option>
              <option value="weekly">Every week</option>
              <option value="monthly">Every month</option>
              <option value="yearly">Every year</option>
              {draft.repeat === "custom" && <option value="custom">Custom repeat (unchanged)</option>}
            </select>
          </div>
          <label className={styles.toggle}>
            <span>
              <b>Teams meeting</b>
              <small>Adds an online meeting link</small>
            </span>
            <input type="checkbox" className={styles.switch} checked={draft.online} onChange={(e) => patchDraft({ online: e.target.checked, location: e.target.checked ? TEAMS : draft.location === TEAMS ? "" : draft.location })} />
          </label>
          <div className={styles.field}>
            <label>Location</label>
            <input placeholder="Add a location" value={draft.location} onChange={(e) => patchDraft({ location: e.target.value })} />
          </div>
          <div className={styles.field}>
            <label>People</label>
            <input placeholder="Add people, separated by commas" value={draft.attendees} onChange={(e) => patchDraft({ attendees: e.target.value })} />
          </div>
          <div className={styles.field}>
            <label>Description</label>
            <textarea rows={4} value={draft.notes} onChange={(e) => patchDraft({ notes: e.target.value })} />
          </div>
          {draftErr && (
            <div className={styles.err} role="alert">
              {draftErr}
            </div>
          )}
        </div>
      )}
    </section>
  );

  /* ---------- search page ---------- */
  const searchPage = (
    <section className={`${styles.page} ${searchOpen && stack.includes("search") ? styles.pageOn : ""}`} aria-hidden={!searchOpen}>
      <header className={styles.searchBar}>
        <button type="button" className={styles.iconBtn} aria-label="Back" onClick={popPage}>
          <ArrowLeft24Regular />
        </button>
        <div className={styles.searchField}>
          <input autoFocus={searchOpen} type="search" placeholder="Search events" value={q} onChange={(e) => setQ(e.target.value)} />
          {q && (
            <button type="button" className={styles.iconBtn} aria-label="Clear" onClick={() => setQ("")}>
              <Dismiss24Regular />
            </button>
          )}
        </div>
      </header>
      <div className={styles.pageScroll}>
        {!q.trim() ? (
          <p className={styles.hint}>Search by title, place, organiser or guest in the weeks on screen.</p>
        ) : results.length === 0 ? (
          <p className={styles.hint}>No events match “{q.trim()}”.</p>
        ) : (
          results.map((e) => (
            <div key={`${e.id}|${e.date}`}>
              <div className={styles.dayHead}>
                <span>{longDate(parseYmd(e.date))}</span>
              </div>
              {eventCard(e, false)}
            </div>
          ))
        )}
      </div>
    </section>
  );

  /* ---------- render ---------- */
  return (
    <div className={styles.mcRoot}>
      <div className={styles.screen}>
        {header}
        {view === "agenda" ? agenda : dayView}
        <button type="button" className={styles.fab} aria-label="New event" onClick={openNew}>
          <Add24Regular />
        </button>
      </div>

      <nav className={styles.tabBar} aria-label="Calendar navigation">
        <button type="button" className={styles.tabBtn} onClick={openMail}>
          <span className={styles.tabIcon}>
            <Mail24Regular />
          </span>
          <span>Email</span>
        </button>
        <button type="button" className={`${styles.tabBtn} ${styles.tabOn}`} aria-current="page">
          <span className={styles.tabIcon}>
            <CalendarLtr24Filled />
          </span>
          <span>Calendar</span>
        </button>
        {onExit && (
          <button type="button" className={styles.tabBtn} onClick={onExit}>
            <span className={styles.tabIcon}>
              <Grid24Regular />
            </span>
            <span>All apps</span>
          </button>
        )}
      </nav>

      {/* calendars drawer */}
      <div className={`${styles.scrim} ${drawer ? styles.scrimOn : ""}`} onClick={() => setDrawer(false)} />
      <aside className={`${styles.drawer} ${drawer ? styles.drawerOn : ""}`} aria-hidden={!drawer}>
        {onExit && (
          <button type="button" className={styles.drawerApps} onClick={() => { setDrawer(false); onExit(); }}>
            <Grid24Regular />
            <span>All apps</span>
          </button>
        )}
        <div className={styles.drawerScroll}>
          <div className={styles.drawerSec}>
            <span>My calendars</span>
            <button type="button" aria-label="Add calendar" onClick={() => { setDrawer(false); setSheet("addCal"); }}>
              <CalendarAdd24Regular />
            </button>
          </div>
          <ul className={styles.calList}>
            {cals.length === 0 && <li className={styles.hint}>{status === "loading" ? "Loading…" : "No calendars yet."}</li>}
            {cals.map((c) => (
              <li key={c.id}>
                <button type="button" className={styles.calRow} onClick={() => toggleCal(c.id)} aria-pressed={c.on}>
                  <span className={styles.check} style={{ background: c.on ? c.color : "transparent", borderColor: c.color }}>
                    {c.on && <Checkmark24Regular />}
                  </span>
                  <span className={styles.calNameTxt}>{c.name}</span>
                  {!c.canEdit && <small>View only</small>}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </aside>

      {eventPage}
      {editPage}
      {searchPage}

      {/* sheets */}
      <div className={`${styles.scrim} ${sheet ? styles.scrimOn : ""}`} onClick={() => setSheet(null)} />
      <div className={`${styles.sheet} ${sheet ? styles.sheetOn : ""}`} role="dialog" aria-modal="true">
        <div className={styles.grab} />
        {sheet === "account" && (
          <>
            <div className={styles.sheetWho}>
              <Avatar name={displayName || email || "?"} size={56} />
              <div>
                <b>{displayName || "Signed in"}</b>
                <small>{email}</small>
              </div>
            </div>
            {onExit && (
              <button type="button" className={styles.secondary} onClick={() => { setSheet(null); onExit(); }}>
                <Building24Regular /> All apps
              </button>
            )}
          </>
        )}
        {sheet === "addCal" && (
          <>
            <h3 className={styles.sheetTitle}>New calendar</h3>
            <input className={styles.textField} placeholder="Calendar name" value={newCal.name} onChange={(e) => setNewCal({ ...newCal, name: e.target.value })} autoFocus />
            <div className={styles.swatches}>
              {SWATCHES.map((c) => (
                <button key={c} type="button" aria-label={`Colour ${c}`} className={newCal.color === c ? styles.swOn : ""} style={{ background: c }} onClick={() => setNewCal({ ...newCal, color: c })}>
                  {newCal.color === c && <Checkmark24Regular />}
                </button>
              ))}
            </div>
            <button type="button" className={styles.primary} disabled={!newCal.name.trim()} onClick={() => void saveCal()}>
              Add calendar
            </button>
          </>
        )}
        {sheet === "delete" && delEv && (
          <>
            <h3 className={styles.sheetTitle}>Delete repeating event</h3>
            <p className={styles.sheetText}>“{delEv.title}” repeats. Which events do you want to delete?</p>
            <button type="button" className={styles.secondary} onClick={() => void removeEvent(delEv, "this")}>
              Only this event
            </button>
            <button type="button" className={styles.danger} onClick={() => void removeEvent(delEv, "all")}>
              All events in the series
            </button>
          </>
        )}
      </div>

      {toast && (
        <div className={styles.toast} role="status">
          <span>{toast.msg}</span>
          {toast.undo && (
            <button type="button" onClick={() => { toast.undo?.(); setToast(null); }}>
              Undo
            </button>
          )}
        </div>
      )}
    </div>
  );
};

/* Page: takes the signed-in user from the suite's central auth. */
const MobileCalendar: React.FC<{ onExit?: () => void }> = ({ onExit }) => {
  const { user } = useAuth();
  const token = user?.token || readStoredToken();
  const name = `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim();
  return <MobileCalendarClient token={token ?? null} displayName={name} email={user?.email ?? ""} {...(onExit ? { onExit } : {})} />;
};

export default MobileCalendar;